import { SearchIcon } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { Input } from '@/components/ui/input.js';

import {
  SEARCH_NOTHING_MATCHED,
  SharePointSearchNote,
  SharePointSearchResultList,
} from './BrowseLevelList.js';
import { isSharePointNotConnected } from './browse-error.js';
import type { SharePointConnectionChoice } from './connection-choice.js';
import { SEARCH_DEBOUNCE_MS, useSharePointFileSearch } from './file-search.js';
import type { SharePointKnowledgeMark } from './knowledge-mark.js';
import { SharePointNotConnectedNotice } from './SharePointNotConnected.js';
import {
  searchHitRow,
  type SharePointFileSelection,
  type SharePointRow,
} from './sharepoint-row.js';

/**
 * One lane of the fan: which SharePoint to ask, and what to call the answers. `connectionId` is `null` and
 * the label is missing on the single lane a surface falls back to when it knows of no connection by name —
 * the service then resolves the workspace's shared one, which is what every search did before connections
 * had ids.
 */
interface SearchLane {
  connectionId: string | null;
  label?: string;
}

/** The lane a surface with no named connections searches. */
const RESOLVED_LANE: SearchLane = { connectionId: null };

/** What a group tells the surface about itself, so the whole search can be summed up once. */
interface GroupOutcome {
  loading: boolean;
  /** No rows, no error — this connection has answered and found nothing. */
  empty: boolean;
  /** This connection answered 412: the workspace has no SharePoint connection at all. */
  notConnected: boolean;
}

/**
 * How a group is remembered while the surface holds the outcomes of all of them. The QUERY is part of it:
 * without it a new query would be judged against the previous one's answers, and "nothing matched" would
 * flash over hits that are on their way.
 */
function outcomeKey(lane: SearchLane, query: string): string {
  return `${query}\u0000${lane.connectionId ?? ''}`;
}

/**
 * The search box for a surface that shows SharePoint files, and the hits it finds — across EVERY
 * SharePoint the caller reaches in this workspace, one request per connection.
 *
 * Searching belongs to the surface rather than to the tree inside it, because a fan is wider than any one
 * tree by definition: a document in the caller's personal SharePoint has to be findable from a surface
 * standing in the workspace's shared one. The tree keeps its own in-tree search where a fan would be noise
 * instead of help — the knowledge wizard and the transfer screen, where a person is choosing a folder in
 * one named SharePoint.
 *
 * Two surfaces share it: the Artifacts section's SharePoint tab, whose rows are links and actions, and the
 * composer's Add-artifacts dialog, whose file rows are pick toggles. Neither is described here — what a row
 * offers comes in through {@link renderItemAction}, picking through {@link fileSelection} — so the two
 * cannot grow different search behaviour.
 *
 * What the surface itself shows when the box is empty is {@link children}: the connection level, a tree,
 * whatever it was showing. Clearing the box puts it back untouched — the same position and the same names
 * the user walked through — because nothing here ever writes to it.
 *
 * "Untouched" needs one thing from the surfaces, though: children are UNMOUNTED while hits are on screen,
 * since a row of the open level must not stay findable behind the results. So anything a tree holds that has
 * to outlive a search belongs ABOVE this component — which is what `useRememberedPath` is for, and why the
 * Artifacts tab owns the walked-through names instead of its tree.
 *
 * The group's heading is the connection's own name, and that name is the whole source mark this feature
 * needs: a hit says where it came from by which group it is in. With a single connection there is no
 * heading and the list looks exactly as it did before the fan existed.
 */
export function SharePointSearchSurface({
  workspaceId,
  connections,
  onOpenFolder,
  onOpenFile,
  renderItemAction,
  knowledgeMark,
  fileSelection,
  children,
}: {
  workspaceId: string;
  /**
   * The SharePoints to search, in the order their groups appear. An empty list is not "search nothing" —
   * it is a surface that knows of no connection by name, and the one request then leaves the choice to the
   * service. A surface whose list is not read YET renders no box at all rather than passing an empty one.
   */
  connections: readonly SharePointConnectionChoice[];
  /**
   * A folder hit the user chose to open. The row carries the connection it was found through, so a surface
   * that navigates can move to the right tenant and not to the same Graph ids in the wrong one.
   */
  onOpenFolder: (row: SharePointRow) => void;
  /** A file hit the user chose to open in the file viewer. Left off, file hits are not clickable. */
  onOpenFile?: (row: SharePointRow) => void;
  renderItemAction?: (row: SharePointRow) => ReactNode;
  /**
   * Whether a hit is already a workspace knowledge source, answered per ADDRESS — the hit's own, including
   * its connection, because a `drive_id` means nothing outside the tenant it came from.
   */
  knowledgeMark?: SharePointKnowledgeMark;
  /** Set by a surface whose file rows are pick toggles rather than links to something else. */
  fileSelection?: SharePointFileSelection;
  /** What the surface shows while the box is empty. */
  children: ReactNode;
}): JSX.Element {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [outcomes, setOutcomes] = useState<Record<string, GroupOutcome>>({});

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  /**
   * Hits are on screen only while the box HAS something in it and that something has settled into a query.
   * Reading both is what makes clearing the box instant — waiting for the debounce would leave the old
   * hits up for another quarter second — while typing keeps the surface's own content until there is
   * something to put in its place.
   */
  const searching = searchInput.trim() !== '' && query !== '';

  const report = useCallback(
    (key: string, outcome: GroupOutcome | null): void => {
      setOutcomes((previous) => {
        const current = previous[key];
        if (outcome === null) {
          if (current === undefined) return previous;
          const next = { ...previous };
          delete next[key];
          return next;
        }
        if (
          current !== undefined &&
          current.loading === outcome.loading &&
          current.empty === outcome.empty &&
          current.notConnected === outcome.notConnected
        ) {
          return previous;
        }
        return { ...previous, [key]: outcome };
      });
    },
    [],
  );

  function clearSearch(): void {
    setSearchInput('');
    setQuery('');
  }

  const lanes: readonly SearchLane[] =
    connections.length > 0 ? connections : [RESOLVED_LANE];
  const reported = lanes
    .map((lane) => outcomes[outcomeKey(lane, query)])
    .filter((outcome): outcome is GroupOutcome => outcome !== undefined);
  /** A lane that has not reported yet counts as loading, so no verdict is announced before its answer. */
  const anyLoading =
    reported.length < lanes.length ||
    reported.some((outcome) => outcome.loading);
  const everyGroupEmpty =
    reported.length === lanes.length &&
    reported.every((outcome) => outcome.empty);
  const notConnected = reported.some((outcome) => outcome.notConnected);

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="relative">
        <SearchIcon
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search files by name or contents"
          aria-label="Search SharePoint files"
          autoComplete="off"
          className="pl-9"
        />
      </div>

      {!searching && children}

      {searching && <SharePointSearchNote />}

      {searching && notConnected && (
        <SharePointNotConnectedNotice workspaceId={workspaceId} />
      )}

      {searching &&
        lanes.map((lane) => (
          <ConnectionSearchGroup
            key={lane.connectionId ?? ''}
            workspaceId={workspaceId}
            lane={lane}
            query={query}
            named={lanes.length > 1}
            onReport={report}
            onOpenFolder={(row) => {
              clearSearch();
              onOpenFolder(row);
            }}
            onOpenFile={onOpenFile}
            renderItemAction={renderItemAction}
            knowledgeMark={knowledgeMark}
            fileSelection={fileSelection}
          />
        ))}

      {searching && !notConnected && everyGroupEmpty && !anyLoading && (
        <p className="px-2 py-3 text-sm text-muted-foreground">
          {SEARCH_NOTHING_MATCHED}
        </p>
      )}
    </div>
  );
}

/**
 * What one connection found. It is a component rather than a loop body in the surface because the search
 * hook is called here: React needs the order of hooks to be stable, and a hook per row of a list that can
 * change length is not.
 *
 * A group that has answered with nothing renders nothing at all — not even its heading. "Nothing matched"
 * is one sentence for the whole search, said by the surface once every group is in, so a group announcing
 * its own emptiness beside a neighbour's hits would be both noise and a contradiction. A group whose
 * connection answered 412 is silent for the same reason: the workspace has no SharePoint at all, and the
 * surface says that once, as an invitation rather than as a failure.
 *
 * It is its own scroll box: a long file name scrolls sideways inside the group instead of widening the
 * page, which makes the box a keyboard stop and so gives it a visible focus ring.
 */
function ConnectionSearchGroup({
  workspaceId,
  lane,
  query,
  named,
  onReport,
  onOpenFolder,
  onOpenFile,
  renderItemAction,
  knowledgeMark,
  fileSelection,
}: {
  workspaceId: string;
  lane: SearchLane;
  query: string;
  /** Whether this group says which SharePoint it is. A single lane needs no name: there is no other. */
  named: boolean;
  onReport: (key: string, outcome: GroupOutcome | null) => void;
  onOpenFolder: (row: SharePointRow) => void;
  onOpenFile?: (row: SharePointRow) => void;
  renderItemAction?: (row: SharePointRow) => ReactNode;
  knowledgeMark?: SharePointKnowledgeMark;
  fileSelection?: SharePointFileSelection;
}): JSX.Element | null {
  const headingId = useId();
  const list = useSharePointFileSearch(workspaceId, query, lane.connectionId);
  const key = outcomeKey(lane, query);
  const { isLoading, isError, error, rows } = list;
  const notConnected = isSharePointNotConnected(error);
  const empty = !isLoading && !isError && rows.length === 0;

  useEffect(() => {
    onReport(key, { loading: isLoading, empty, notConnected });
  }, [onReport, key, isLoading, empty, notConnected]);

  useEffect(() => () => onReport(key, null), [onReport, key]);

  if (empty || notConnected) return null;

  const heading = named && lane.label !== undefined;
  return (
    <section
      aria-labelledby={heading ? headingId : undefined}
      tabIndex={0}
      className="flex min-w-0 flex-col gap-1 overflow-x-auto rounded-xl border border-border p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      {heading && (
        <h3
          id={headingId}
          className="px-2 py-1 text-caption font-medium text-muted-foreground"
        >
          {lane.label}
        </h3>
      )}
      <SharePointSearchResultList
        list={list}
        connectionId={lane.connectionId}
        onOpenFolder={(hit) =>
          onOpenFolder(searchHitRow(hit, lane.connectionId))
        }
        onOpenFile={onOpenFile}
        renderItemAction={renderItemAction}
        knowledgeMark={
          knowledgeMark
            ? (row) =>
                knowledgeMark({
                  connectionId: row.connectionId,
                  driveId: row.driveId,
                  itemId: row.item.itemId,
                })
            : undefined
        }
        fileSelection={fileSelection}
      />
    </section>
  );
}
